import { MenuItem } from '@szhsin/react-menu'
import { useState } from 'react'
import { ExternalCalendar } from '../../../utils/calendar'
import { ExternalCalendarList } from './ExternalCalendarList'

type UnifiedAccountItemProps = {
  accountName: string
  accountEmail?: string
  calendars: ExternalCalendar[]
  onRemove: () => void
  provider: 'apple' | 'outlook' | 'google'
  profileImage?: string
  isExpanded?: boolean
  onToggleExpansion?: () => void
}

export function UnifiedAccountItem({
  accountName,
  accountEmail,
  calendars,
  onRemove,
  provider,
  profileImage,
  isExpanded: controlledExpanded,
  onToggleExpansion
}: UnifiedAccountItemProps) {
  const [localExpanded, setLocalExpanded] = useState(false)
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : localExpanded
  
  const handleToggle = () => {
    if (onToggleExpansion) {
      onToggleExpansion()
    } else {
      setLocalExpanded(!localExpanded)
    }
  }

  // Get provider colors
  const getProviderColors = () => {
    switch (provider) {
      case 'apple':
        return {
          bg: 'bg-black dark:bg-white',
          text: 'text-white dark:text-black',
          gradient: 'bg-gradient-to-br from-gray-700 to-black dark:from-gray-200 dark:to-white'
        }
      case 'outlook':
        return {
          bg: 'bg-blue-600',
          text: 'text-white',
          gradient: 'bg-gradient-to-br from-blue-500 to-blue-700'
        }
      case 'google':
        return {
          bg: 'bg-blue-500',
          text: 'text-white',
          gradient: 'bg-gradient-to-br from-blue-400 to-blue-600'
        }
    }
  }

  const colors = getProviderColors()
  const displayName = accountName || accountEmail?.split('@')[0] || 'User'
  const initial = displayName.charAt(0).toUpperCase()

  // Get provider icon
  const getProviderIcon = () => {
    switch (provider) {
      case 'apple':
        return <i className='fab fa-apple text-sm' />
      case 'outlook':
        return <i className='fab fa-microsoft text-xs' />
      case 'google':
        return <span className='text-xs font-semibold'>{initial}</span>
    }
  }

  return (
    <>
      <MenuItem
        onClick={(event) => {
          event.keepOpen = true
          handleToggle()
        }}
        className='group hover:bg-gray-100 dark:hover:bg-gray-800'
      >
        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center gap-3'>
            {/* Profile image or provider icon */}
            <div className='relative'>
              {profileImage && provider === 'google' ? (
                <img
                  src={profileImage}
                  alt={displayName}
                  className='h-8 w-8 rounded-full object-cover'
                />
              ) : (
                <div className={`flex h-8 w-8 items-center justify-center rounded-full ${colors.gradient} ${provider === 'apple' ? 'text-white dark:text-black' : 'text-white'}`}>
                  {getProviderIcon()}
                </div>
              )}
              {/* Always show green status for non-Google accounts */}
              <div className='absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500 dark:border-gray-900' />
            </div>
            <div className='flex flex-col'>
              <span className='text-sm font-medium'>
                {displayName}
              </span>
              {accountEmail && (
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                  {accountEmail}
                </span>
              )}
            </div>
          </div>
          <i
            className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} text-xs text-gray-400 transition-transform duration-200`}
          />
        </div>
      </MenuItem>

      {isExpanded && (
        <>
          {/* Calendars list */}
          {calendars.length > 0 && (
            <div className='ml-8'>
              <div className='ml-2'>
                <ExternalCalendarList items={calendars} />
              </div>
            </div>
          )}

          {/* Remove account button */}
          <div className='ml-8'>
            <MenuItem onClick={onRemove} className='group ml-2'>
              <div className='flex items-center gap-3'>
                <div className='flex h-5 w-5 shrink-0 items-center justify-center'>
                  <i
                    className='fa-regular fa-trash-can text-sm text-red-500'
                    aria-hidden='true'
                  />
                </div>
                <span className='text-sm text-red-600 dark:text-red-400'>
                  Remove account
                </span>
              </div>
            </MenuItem>
          </div>
        </>
      )}
    </>
  )
}